import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import {
	GridActionsCellItem,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowParams,
} from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePaginatedPayments } from '../../../api/PaymentsApi';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { CurrencyCode } from '../../../types/currencyCode';
import { Payment } from '../../../types/payments';
import { formatCurrency } from '../../../util/util';
import { LinkCell } from '../../grid';
import GridWithToolbar from '../../grid/GridWithToolbar';
import ApplyPaymentModal from '../components/ApplyPaymentModal';
import ApplyPositivePaymentModal from '../components/ApplyPositivePaymentModal';
import PaymentStateChip from '../components/PaymentStateChip';
import {
	PAYMENTS_PAGE__ADD_PAYMENT_BUTTON,
	PAYMENTS_PAGE__APPLY_PAYMENT_BUTTON,
	PAYMENTS_PAGE__TABLE_ACTION__EDIT,
	PAYMENTS_PAGE__TABLE_HEADER__AMOUNT,
	PAYMENTS_PAGE__TABLE_HEADER__CREATED_DATE,
	PAYMENTS_PAGE__TABLE_HEADER__PAYMENT_ID,
	PAYMENTS_PAGE__TABLE_HEADER__STATUS,
	PAYMENTS_PAGE__TITLE,
} from '../data/constants';

interface ApplyPaymentInfo {
	open: boolean;
	payment?: Payment;
}

const PaymentsPage = () => {
	const authData = useAuthContext();
	const params = useParams();
	const policyLocator = params.policyId;
	const policyholderLocator = params.policyholderId;
	const navigate = useNavigate();
	const { hasPermission } = authData;
	const authToken = authData.auth?.authorizationToken;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const [page, setPage] = React.useState({
		page: 0,
		pageSize: 25,
	});
	// const [pagingToken, setPagingToken] = useState<string>();

	const [applyPaymentInfo, setOpenApplyPaymentInfo] =
		useState<ApplyPaymentInfo>(null);

	const {
		data: paymentsResponse,
		isLoading,
		fetchNextPage,
		fetchPreviousPage,
		isFetchingNextPage,
		hasNextPage,
	} = usePaginatedPayments(
		page.pageSize,
		authToken,
		policyholderLocator,
		policyLocator,
	);

	const currPage = paymentsResponse?.pages[page.page];
	const payments = currPage?.data.payments || [];

	const StateChip = (params: GridRenderCellParams<Payment>) => {
		return <PaymentStateChip state={params.row.state} />;
	};

	const getRowActions = (params: GridRowParams<Payment>) => {
		const rowActions = [];
		if (params.row.state === 'draft' && hasPermission('payment.update')) {
			rowActions.push(
				<GridActionsCellItem
					icon={<EditIcon />}
					label={PAYMENTS_PAGE__TABLE_ACTION__EDIT}
					onClick={() => navigate(`${params.row.locator}/edit`)}
				/>,
			);
		}
		if (params.row.state === 'ready' && hasPermission('invoice.pay')) {
			rowActions.push(
				<GridActionsCellItem
					icon={<AttachMoneyIcon />}
					label={PAYMENTS_PAGE__APPLY_PAYMENT_BUTTON}
					onClick={() => {
						setOpenApplyPaymentInfo({
							open: true,
							payment: payments.find((p) => p.locator === params.row.locator),
						});
					}}
				/>,
			);
		}
		return rowActions;
	};

	const columns: GridColDef[] = [
		{
			field: 'displayId',
			headerName: PAYMENTS_PAGE__TABLE_HEADER__PAYMENT_ID,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => (
				<LinkCell label={params.value} to={params.row.locator} />
			),
		},
		{
			field: 'amount',
			headerName: PAYMENTS_PAGE__TABLE_HEADER__AMOUNT,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => params.row.amountFormatted,
		},
		{
			field: 'state',
			headerName: PAYMENTS_PAGE__TABLE_HEADER__STATUS,
			flex: 0.2,
			sortable: false,
			renderCell: StateChip,
		},
		{
			field: 'createdTimestamp',
			headerName: PAYMENTS_PAGE__TABLE_HEADER__CREATED_DATE,
			sortable: false,
			flex: 0.2,
		},
		{
			field: 'actions',
			type: 'actions',
			align: 'right',
			flex: 0.2,
			sortable: false,
			getActions: getRowActions,
		},
	];

	const handlePageChange = (model: GridPaginationModel) => {
		if (model.pageSize !== page.pageSize) {
			setPage({
				page: 0,
				pageSize: model.pageSize,
			});
		} else {
			setPage({
				page: model.page,
				pageSize: model.pageSize,
			});
			if (model.page > page.page) {
				fetchNextPage();
			}
			if (model.page < page.page) {
				fetchPreviousPage();
			}
		}
	};

	const formattedData = payments.map((payment) => {
		return {
			...payment,
			amount: payment.amount,
			amountFormatted: formatCurrency(
				parseFloat(payment.amount),
				payment.amountCurrency as CurrencyCode,
			),
			createdTimestamp: payment.createdTimestamp
				? authData.getZonedFormattedDateString(payment.createdTimestamp)
				: '',
		};
	});

	const renderApplyPaymentModal = () => {
		if (
			parseFloat(applyPaymentInfo.payment.amount) < 0 ||
			!cashManagementEnabled
		) {
			return (
				<ApplyPaymentModal
					open={applyPaymentInfo.open}
					setOpen={(val: boolean) => {
						setOpenApplyPaymentInfo({
							open: val,
						});
					}}
					payment={applyPaymentInfo.payment}
					paymentsPageSize={page.pageSize}
				/>
			);
		}
		return (
			<ApplyPositivePaymentModal
				open={applyPaymentInfo.open}
				setOpen={(val: boolean) => {
					setOpenApplyPaymentInfo({
						open: val,
					});
				}}
				payment={applyPaymentInfo.payment}
				paymentsPageSize={page.pageSize}
			/>
		);
	};

	const canCreatePayment = hasPermission('payment.create');
	return (
		<>
			<GridWithToolbar
				rowId='locator'
				title={PAYMENTS_PAGE__TITLE}
				key={payments?.length}
				data={formattedData || []}
				columns={columns}
				loading={isLoading || isFetchingNextPage}
				buttonColor='primary'
				buttonText={PAYMENTS_PAGE__ADD_PAYMENT_BUTTON}
				onButtonClick={canCreatePayment ? () => navigate('new') : null}
				paginationMode='server'
				rowCount={1000}
				paginationModel={page}
				onPaginationModelChange={handlePageChange}
				hasNextPage={
					hasNextPage || paymentsResponse?.pages?.length > page.page + 1
				}
			/>
			{applyPaymentInfo?.open && applyPaymentInfo?.payment
				? renderApplyPaymentModal()
				: null}
		</>
	);
};

export default PaymentsPage;
